import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useAuthStore, useDealPortfolioStore, useFavoriteStore, useLeadStore, useLeaseCompStore, useLocationStore, useMapStore, usePipedriveStore, useRecentStore, useSaleCompStore, useSavedViewStore, useTenantInMarketStore, useViewSettingsStore } from '.';
import './App.css';
import Account from './components/account/Account';
import AddLabel from './components/AddLabel';
import CompanyDetails from "./components/companies/CompanyDetails";
import CompanyEdit from "./components/companies/CompanyEdit";
import ContextMenu from './components/ContextMenu';
import DealCreateFromLead from './components/deals/DealCreateFromLead';
import DealCreateFromProperty from './components/deals/DealCreateFromProperty';
import DealDetails from './components/deals/DealDetails';
import DealEdit from './components/deals/DealEdit';
import Deals from './components/deals/Deals';
import DriveTime from './components/DriveTime';
import GlobalFilters from './components/globalFilters/GlobalFilters';
import Header from './components/header/Header';
import Insights from './components/insights/Insights';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import ChangeLeadStatus from './components/leads/ChangeLeadStatus';
import LeadDetails from './components/leads/LeadDetails';
import LeadEdit from './components/leads/LeadEdit';
import SetReminder from './components/leads/SetReminder';
import LeaseCompCreate from './components/leaseComps/LeaseCompCreate';
import LeaseCompDetails from './components/leaseComps/LeaseCompDetails';
import LeaseCompEdit from './components/leaseComps/LeaseCompEdit';
import LeaseComps from './components/leaseComps/LeaseComps';
import ListController from './components/ListController';
import LocationCreate from './components/locations/LocationCreate';
import ForgotPassword from './components/login/ForgotPassword';
import Login from './components/login/Login';
import ResetPassword from './components/login/ResetPassword';
import UserActivation from './components/login/UserActivation';
import MainLayout from './components/MainLayout';
import Map from './components/Map';
import StatusPanelMapControl from './components/mapControls/StatusPanelMapControl';
import McpAuthorize from './components/mcp/McpAuthorize';
import Navbar from './components/Navbar';
import AiGradeScreenshotsCollector from './components/property/AiGradeScreenshotsCollector';
import PropertyDetails from './components/property/PropertyDetails';
import SaleCompCreate from './components/saleComps/SaleCompCreate';
import SaleCompDetails from './components/saleComps/SaleCompDetails';
import SaleCompEdit from './components/saleComps/SaleCompEdit';
import SaleComps from './components/saleComps/SaleComps';
import SavedViewPreview from './components/savedViews/SavedViewPreview';
import SavedViews from './components/savedViews/SavedViews';
import FullScreenSpinner from './components/spinners/FullScreenSpinner';
import StreetView from './components/StreetView';
import TenantInMarketCreate from './components/tenantsInMarket/TenantInMarketCreate';
import TenantInMarketDetails from './components/tenantsInMarket/TenantInMarketDetails';
import TenantInMarketEdit from './components/tenantsInMarket/TenantInMarketEdit';
import TenantsInMarket from './components/tenantsInMarket/TenantsInMarket';
import ToastContainer from './components/ToastContainer';

function App() {
    const location = useLocation();
    const authStore = useAuthStore();
    const mapStore = useMapStore();
    const leadStore = useLeadStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const tenantInMarketStore = useTenantInMarketStore();
    const savedViewStore = useSavedViewStore();
    const pipedriveStore = usePipedriveStore();
    const locationStore = useLocationStore();
    const favoriteStore = useFavoriteStore();
    const recentStore = useRecentStore();
    const viewSettingsStore = useViewSettingsStore();
    const dealPortfolioStore = useDealPortfolioStore();

    useEffect(() => {
        authStore.initialize();
    }, []);

    useEffect(() => {
        if (authStore.isAuthenticated) {
            mapStore.initialize();
            leaseCompStore.initialize();
            saleCompStore.initialize();
            tenantInMarketStore.initialize();
            savedViewStore.initialize();
            pipedriveStore.initialize();
            favoriteStore.initialize();
            recentStore.initialize();
            dealPortfolioStore.initialize();

            if (!viewSettingsStore.isMobile) {
                leadStore.initialize();
                locationStore.initialize();
            }
        }
    }, [authStore.isAuthenticated]);

    if (authStore.isInitialAppLoading)
        return <FullScreenSpinner />;

    return (
        authStore.isAuthenticated ? (
            viewSettingsStore.isMobile ? (
                <Routes>
                    <Route path="/*" element={(
                        <>
                            <Map />
                            <Header />
                            <MainLayout>
                                <Outlet />
                            </MainLayout>
                            <Navbar />
                            <AiGradeScreenshotsCollector />
                        </>
                    )}>
                        <Route index element={<></>} />
                        <Route path="property/:longitude/:latitude" element={<PropertyDetails />} />
                        <Route path="deals" element={<Deals />} />
                        <Route path="deals/:id" element={<DealDetails />} />
                        <Route path="leaseComps" element={<LeaseComps />} />
                        <Route path="leaseComps/:id" element={<LeaseCompDetails />} />
                        <Route path="saleComps" element={<SaleComps />} />
                        <Route path="saleComps/:id" element={<SaleCompDetails />} />
                        <Route path="tenantsInMarket" element={<TenantsInMarket />} />
                        <Route path="tenantsInMarket/:id" element={<TenantInMarketDetails />} />
                        <Route path="userSettings" element={<Account />} />
                        <Route path="mcp/authorize" element={<McpAuthorize />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Route>
                </Routes>
            ) : (
                <Routes>
                    <Route path="/*" element={(
                        <>
                            <Map />
                            <Header />
                            <MainLayout>
                                <Navbar />
                                <ListController />
                                <Outlet />
                            </MainLayout>
                            <StatusPanelMapControl />
                            <ToastContainer />
                            <KeyboardShortcuts />
                            <ContextMenu />
                            <GlobalFilters />
                            <StreetView />
                            <DriveTime />
                            <SetReminder />
                            <AddLabel />
                            <ChangeLeadStatus />
                            <SavedViewPreview />
                            <AiGradeScreenshotsCollector />
                        </>
                    )}>
                        <Route index element={<></>} />
                        <Route path="property/:longitude/:latitude" element={<PropertyDetails />} />
                        <Route path="deals/:id" element={<DealDetails />} />
                        <Route path="deals/:id/edit" element={<DealEdit />} />
                        <Route path="deals/createFromProperty/:longitude/:latitude" element={<DealCreateFromProperty />} />
                        <Route path="deals/createFromLead/:leadId" element={<DealCreateFromLead />} />
                        <Route path="leads/:id" element={<LeadDetails />} />
                        <Route path="leads/:id/edit" element={<LeadEdit />} />
                        <Route path="leads/create/:longitude/:latitude" element={<></>} />
                        <Route path="leaseComps/:id" element={<LeaseCompDetails />} />
                        <Route path="leaseComps/:id/edit" element={<LeaseCompEdit />} />
                        <Route path="leaseComps/create/:longitude/:latitude" element={<LeaseCompCreate />} />
                        <Route path="saleComps/:id" element={<SaleCompDetails />} />
                        <Route path="saleComps/:id/edit" element={<SaleCompEdit />} />
                        <Route path="saleComps/create/:longitude/:latitude" element={<SaleCompCreate />} />
                        <Route path="companies/:id" element={<CompanyDetails />} />
                        <Route path="companies/:id/edit" element={<CompanyEdit />} />
                        <Route path="tenantsInMarket/:id" element={<TenantInMarketDetails />} />
                        <Route path="tenantsInMarket/:id/edit" element={<TenantInMarketEdit />} />
                        <Route path="tenantsInMarket/create" element={<TenantInMarketCreate />} />
                        <Route path="locations/create/:longitude/:latitude" element={<LocationCreate />} />
                        <Route path="insights" element={<Insights />} />
                        <Route path="savedViews" element={<SavedViews />} />
                        <Route path="userSettings" element={<Account />} />
                        <Route path="mcp/authorize" element={<McpAuthorize />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Route>
                </Routes>
            )
        ) : (
            <Routes>
                <Route path="/*" element={(
                    <>
                        <ToastContainer />
                        <Outlet />
                    </>
                )}>
                    <Route path="login" element={<Login />} />
                    <Route path="password/forgot" element={<ForgotPassword />} />
                    <Route path="password/reset" element={<ResetPassword />} />
                    <Route path="activation/:token" element={<UserActivation />} />
                    <Route path="*" element={<Navigate to="/login" state={location} />} />
                </Route>
            </Routes>
        )
    );
}

export default observer(App);
